/**
 * Shared repo-provider helpers. Provider -> emoji is purely cosmetic. These do
 * NOT build or validate URLs - link safety lives server-side (the worker emits a
 * pre-built, allowlisted `vcs_commit_url`); the client only renders it after a
 * cheap origin check (see SourceRepoLink).
 */

/** Sniff a provider from a repo URL (used where only a URL is known). */
export function detectRepoProvider(url: string | null): 'github' | 'gitlab' | null {
  if (!url) return null
  if (url.includes('github.com')) return 'github'
  if (url.includes('gitlab.com') || url.includes('gitlab')) return 'gitlab'
  return null
}


/** Hosts we render as clickable links. Exact hostname match only - a loose
 *  substring sniff would accept https://gitlab.evil.com or https://evil.com/gitlab.
 *  Single source of truth for both repo-root links (WorkspaceProvidersTab) and
 *  server-built commit links (SourceRepoLink). Mirrors the worker's host allowlist. */
const SAFE_REPO_HOSTS = new Set(['github.com', 'gitlab.com'])

/**
 * True iff `url` is safe to render as an `<a href>`: it parses, is https, and its
 * hostname is in the allowlist. Defence-in-depth against stored URLs that predate
 * write-side validation or use a non-navigable scheme (http:/ssh:/javascript:).
 * The value is NEVER trusted just because it was stored or server-supplied; render
 * non-matching URLs as plain text. Used for both repo-root URLs (client-stored) and
 * commit URLs (server-built) - same host allowlist, same https requirement. */
export function isSafeRepoUrl(url: string | null | undefined): url is string {
  if (!url) return false
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && SAFE_REPO_HOSTS.has(u.hostname)
  } catch {
    return false
  }
}
